import React, { useState, useEffect } from 'react';
import api from '../services/api';

const WhatsAppSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configStatus, setConfigStatus] = useState({ configured: false, provider: '' });

  // Settings state
  const [settings, setSettings] = useState({
    IsEnabled: true,
    AutoSendSale: true,
    AutoSendPurchase: true,
    SendingMethod: 'api',
    SendToCustomer: true,
    SendToOwner: false,
    OwnerMobile: '',
    CustomMessage: 'Thank you for your business!',
    firmDefaultContact: '',
    firmName: ''
  });

  const [toastMsg, setToastMsg] = useState(null);

  // Test message state
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testFeedback, setTestFeedback] = useState(null);

  const showNotification = (type, text) => {
    setToastMsg({ type, text });
    setTimeout(() => setToastMsg(null), 4000);
  };

  // Fetch current WhatsApp settings and gateway status
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [statusRes, setRes] = await Promise.all([
        api.get('/whatsapp/config-status').catch(() => ({ data: { success: false } })),
        api.get('/whatsapp/settings')
      ]);

      if (statusRes.data?.success) {
        setConfigStatus(statusRes.data);
      }

      if (setRes.data?.success && setRes.data?.settings) {
        const s = setRes.data.settings;
        setSettings({
          IsEnabled: Boolean(s.IsEnabled === 1 || s.IsEnabled === true),
          AutoSendSale: Boolean(s.AutoSendSale === 1 || s.AutoSendSale === true),
          AutoSendPurchase: Boolean(s.AutoSendPurchase === 1 || s.AutoSendPurchase === true),
          SendingMethod: s.SendingMethod === 'web' ? 'web' : 'api',
          SendToCustomer: Boolean(s.SendToCustomer === 1 || s.SendToCustomer === true),
          SendToOwner: Boolean(s.SendToOwner === 1 || s.SendToOwner === true),
          OwnerMobile: s.OwnerMobile || s.firmDefaultContact || '',
          CustomMessage: s.CustomMessage || 'Thank you for your business!',
          firmDefaultContact: s.firmDefaultContact || '',
          firmName: s.firmName || ''
        });

        if (!testPhone && (s.OwnerMobile || s.firmDefaultContact)) {
          setTestPhone(s.OwnerMobile || s.firmDefaultContact);
        }
      }
    } catch (err) {
      console.error('Error loading WhatsApp settings:', err);
      showNotification('danger', 'Failed to load WhatsApp settings from server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save Settings
  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put('/whatsapp/settings', {
        IsEnabled: settings.IsEnabled ? 1 : 0,
        AutoSendSale: settings.AutoSendSale ? 1 : 0,
        AutoSendPurchase: settings.AutoSendPurchase ? 1 : 0,
        SendingMethod: settings.SendingMethod || 'api',
        SendToCustomer: settings.SendToCustomer ? 1 : 0,
        SendToOwner: settings.SendToOwner ? 1 : 0,
        OwnerMobile: settings.OwnerMobile.trim(),
        CustomMessage: settings.CustomMessage.trim()
      });

      if (res.data?.success) {
        showNotification('success', 'WhatsApp settings saved successfully!');
      } else {
        throw new Error(res.data?.message || 'Failed to update settings');
      }
    } catch (err) {
      console.error('Save Settings Error:', err);
      showNotification('danger', err.response?.data?.message || err.message || 'Error saving WhatsApp settings.');
    } finally {
      setSaving(false);
    }
  };

  // Send Test WhatsApp Message
  const handleSendTest = async (e) => {
    e.preventDefault();
    const cleanNumber = testPhone.replace(/\D/g, '');
    if (!cleanNumber || cleanNumber.length < 10) {
      showNotification('danger', 'Please enter a valid 10-digit mobile number for the test message.');
      return;
    }

    setSendingTest(true);
    setTestFeedback(null);

    try {
      const res = await api.post('/whatsapp/test', {
        phone: cleanNumber,
        testMessage: `🔔 *WhatsApp Gateway Verification*\n\nYour WhatsApp sender is active and properly connected for *${settings.firmName || 'Fish Software'}*.\n\nTime: ${new Date().toLocaleString('en-IN')}`
      });

      if (res.data?.success) {
        setTestFeedback({
          type: 'success',
          msg: `✅ Test message successfully sent to ${cleanNumber}!`
        });
        showNotification('success', `Test WhatsApp sent to ${cleanNumber}!`);
      } else {
        throw new Error(res.data?.message || 'Gateway returned an error.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to send test message.';
      setTestFeedback({
        type: 'danger',
        msg: `❌ Error: ${msg}`
      });
      showNotification('danger', msg);
    } finally {
      setSendingTest(false);
    }
  };

  const copyFirmContactToOwner = () => {
    if (settings.firmDefaultContact) {
      setSettings((prev) => ({ ...prev, OwnerMobile: settings.firmDefaultContact }));
      showNotification('success', `Owner mobile set to firm contact: ${settings.firmDefaultContact}`);
    } else {
      showNotification('warning', 'No firm contact number found in Firm Profile.');
    }
  };

  if (loading) {
    return (
      <div className="card shadow-sm border-0 p-5 text-center">
        <div className="spinner-border text-success mx-auto" role="status"></div>
        <div className="mt-3 text-muted fw-semibold">Loading WhatsApp settings...</div>
      </div>
    );
  }

  return (
    <div className="whatsapp-settings-container">
      {toastMsg && (
        <div className={`alert alert-${toastMsg.type} alert-dismissible fade show shadow-sm mb-4`} role="alert">
          {toastMsg.text}
          <button type="button" className="btn-close" onClick={() => setToastMsg(null)}></button>
        </div>
      )}

      {/* Header Bar */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 pb-3 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <div className="bg-success text-white p-2 rounded-3 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
            <i className="bi bi-whatsapp fs-3"></i>
          </div>
          <div>
            <h4 className="fw-bold mb-0 text-dark">WhatsApp Settings (व्हॉट्सॲप सेटींग)</h4>
            <p className="text-muted small mb-0">
              Synicate Gateway, automated bill PDF dispatch, and recipient routing
            </p>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className={`badge ${settings.IsEnabled ? 'bg-success' : 'bg-danger'} px-3 py-2 fs-6 shadow-sm`}>
            <i className={`bi ${settings.IsEnabled ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>
            Sender: {settings.IsEnabled ? 'चालू (ACTIVE)' : 'बंद (OFF)'}
          </span>
          <button
            type="button"
            className="btn btn-primary px-4 fw-semibold shadow-sm d-flex align-items-center gap-2"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status"></span>
                जतन होत आहे...
              </>
            ) : (
              <>
                <i className="bi bi-floppy-fill"></i> सेटींग जतन करा (Save)
              </>
            )}
          </button>
        </div>
      </div>

      <div className="row g-4">
        {/* Left Column: Main Settings Form */}
        <div className="col-12 col-lg-8">
          <form onSubmit={handleSaveSettings}>
            
            {/* Master Switch Card */}
            <div className={`card shadow-sm border-0 mb-4 ${settings.IsEnabled ? 'border-start border-4 border-success' : 'border-start border-4 border-danger'}`}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <h5 className="fw-bold mb-1 text-dark">Master WhatsApp Sender Switch</h5>
                    <p className="text-muted small mb-0">
                      When turned OFF, all automatic and popup WhatsApp bill sends are suspended.
                    </p>
                  </div>
                  <div className="form-check form-switch fs-4">
                    <input
                      className="form-check-input cursor-pointer"
                      type="checkbox"
                      role="switch"
                      id="masterSwitch"
                      checked={settings.IsEnabled}
                      onChange={(e) => setSettings({ ...settings, IsEnabled: e.target.checked })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Auto Print & WhatsApp Popup Card */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-light py-3 border-0">
                <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                  <i className="bi bi-printer-fill text-success"></i>
                  Auto Print & WhatsApp Popup Settings (बील सेव्ह केल्यावर पॉपअप सेटींग)
                </h6>
              </div>
              <div className="card-body p-4">
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input cursor-pointer"
                    type="checkbox"
                    id="autoSendSale"
                    checked={settings.AutoSendSale}
                    onChange={(e) => setSettings({ ...settings, AutoSendSale: e.target.checked })}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="autoSendSale">
                    Sale Invoice Save Action (विक्री बील सेव्ह केल्यावर प्रिंट/व्हॉट्सॲप पॉपअप दाखवा)
                  </label>
                  <div className="text-muted small">
                    Automatically open the Print & WhatsApp popup window immediately after saving a Sale Entry bill.
                  </div>
                </div>

                <hr className="my-3 text-muted" />

                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input cursor-pointer"
                    type="checkbox"
                    id="autoSendPurchase"
                    checked={settings.AutoSendPurchase}
                    onChange={(e) => setSettings({ ...settings, AutoSendPurchase: e.target.checked })}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="autoSendPurchase">
                    Purchase Bill Save Action (खरेदी बील सेव्ह केल्यावर प्रिंट/व्हॉट्सॲप पॉपअप दाखवा)
                  </label>
                  <div className="text-muted small">
                    Automatically open the Print & WhatsApp popup window immediately after saving a Purchase Entry bill.
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp Sending Method Card */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-light py-3 border-0">
                <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                  <i className="bi bi-send-check-fill text-success"></i>
                  WhatsApp Sending Method (व्हॉट्सॲप पाठवण्याची पद्धत)
                </h6>
              </div>
              <div className="card-body p-4">
                <p className="text-muted small mb-3">
                  Choose default method for sending invoices & bills via WhatsApp:
                </p>

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div
                      className={`p-3 rounded-3 border cursor-pointer h-100 transition-all ${settings.SendingMethod === 'api' ? 'border-success bg-success bg-opacity-10 shadow-sm' : 'border-light-subtle bg-white'}`}
                      onClick={() => setSettings({ ...settings, SendingMethod: 'api' })}
                    >
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input cursor-pointer"
                          type="radio"
                          name="sendingMethodRadio"
                          id="methodApi"
                          checked={settings.SendingMethod === 'api'}
                          onChange={() => setSettings({ ...settings, SendingMethod: 'api' })}
                        />
                        <label className="form-check-label fw-bold text-dark cursor-pointer" htmlFor="methodApi">
                          ⚡ WhatsApp Gateway API (ऑटोमॅटिक सर्व्हर API)
                        </label>
                      </div>
                      <div className="text-muted small ms-4">
                        Direct automated dispatch via Synicate / Meta WhatsApp API. Sends PDF document automatically in background without opening WhatsApp app.
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div
                      className={`p-3 rounded-3 border cursor-pointer h-100 transition-all ${settings.SendingMethod === 'web' ? 'border-success bg-success bg-opacity-10 shadow-sm' : 'border-light-subtle bg-white'}`}
                      onClick={() => setSettings({ ...settings, SendingMethod: 'web' })}
                    >
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input cursor-pointer"
                          type="radio"
                          name="sendingMethodRadio"
                          id="methodWeb"
                          checked={settings.SendingMethod === 'web'}
                          onChange={() => setSettings({ ...settings, SendingMethod: 'web' })}
                        />
                        <label className="form-check-label fw-bold text-dark cursor-pointer" htmlFor="methodWeb">
                          🌐 WhatsApp Web / Mobile App (व्हॉट्सॲप वेब / ॲप)
                        </label>
                      </div>
                      <div className="text-muted small ms-4">
                        Opens WhatsApp Web / Desktop / Mobile app directly with formatted bill message ready to hit send.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recipient Routing Card */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-light py-3 border-0">
                <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                  <i className="bi bi-people-fill text-primary"></i>
                  Recipient Routing (बील कोणाला पाठवायचे?)
                </h6>
              </div>
              <div className="card-body p-4">
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input cursor-pointer"
                    type="checkbox"
                    id="sendToCust"
                    checked={settings.SendToCustomer}
                    onChange={(e) => setSettings({ ...settings, SendToCustomer: e.target.checked })}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="sendToCust">
                    Send to Customer / Party (ग्राहकाला व्हॉट्सॲप बील पाठवा)
                  </label>
                  <div className="text-muted small">
                    Dispatches invoice directly to customer mobile number saved on the bill.
                  </div>
                </div>

                <hr className="my-3 text-muted" />

                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input cursor-pointer"
                    type="checkbox"
                    id="sendToOwner"
                    checked={settings.SendToOwner}
                    onChange={(e) => setSettings({ ...settings, SendToOwner: e.target.checked })}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="sendToOwner">
                    Send Owner Copy (मालकाला / दुकानाला बीलाची प्रत पाठवा)
                  </label>
                  <div className="text-muted small mb-2">
                    Sends an exact copy of each created bill to the firm owner mobile number for records.
                  </div>
                </div>

                {settings.SendToOwner && (
                  <div className="mt-3 p-3 bg-light rounded-3 border">
                    <label className="form-label small fw-bold text-dark">
                      Owner WhatsApp Mobile Number (मालकाचा मोबाईल नंबर):
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-white text-muted">+91</span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="10-digit mobile number"
                        value={settings.OwnerMobile}
                        onChange={(e) => setSettings({ ...settings, OwnerMobile: e.target.value })}
                      />
                      {settings.firmDefaultContact && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={copyFirmContactToOwner}
                          title="Copy number from Firm Profile"
                        >
                          फर्म संपर्क वापरा
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Custom Message Card */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-light py-3 border-0">
                <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                  <i className="bi bi-chat-dots-fill text-success"></i>
                  Custom Footer Note (बीलाखालील संदेश)
                </h6>
              </div>
              <div className="card-body p-4">
                <label className="form-label small fw-bold text-dark">
                  Message appended at the bottom of WhatsApp bill text:
                </label>
                <textarea
                  className="form-control"
                  rows="2"
                  placeholder="e.g. Thank you for your business! Please pay balance promptly."
                  value={settings.CustomMessage}
                  onChange={(e) => setSettings({ ...settings, CustomMessage: e.target.value })}
                ></textarea>
              </div>
            </div>

          </form>
        </div>

        {/* Right Column: Gateway Status & Test Message */}
        <div className="col-12 col-lg-4">
          {/* Gateway Status Box */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-light py-3 border-0">
              <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                <i className="bi bi-broadcast text-primary"></i>
                Gateway Connection Status
              </h6>
            </div>
            <div className="card-body p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small text-muted">Gateway Provider:</span>
                <span className="badge bg-dark text-uppercase">{configStatus.provider || 'Synicate BW'}</span>
              </div>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small text-muted">API Connection:</span>
                <span className={`badge ${configStatus.configured ? 'bg-success' : 'bg-warning text-dark'}`}>
                  {configStatus.configured ? 'CONFIGURED' : 'CHECK .ENV'}
                </span>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <span className="small text-muted">PDF Engine:</span>
                <span className="badge bg-info text-dark">PDFKit Vector</span>
              </div>
            </div>
          </div>

          {/* Test WhatsApp Message */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-light py-3 border-0">
              <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                <i className="bi bi-phone-vibrate-fill text-success"></i>
                Send Test WhatsApp Message
              </h6>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSendTest}>
                <div className="mb-3">
                  <label className="form-label small fw-bold text-dark">
                    Test Mobile Number:
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-white text-muted">+91</span>
                    <input
                      type="tel"
                      className="form-control"
                      placeholder="10-digit number"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-outline-success w-100 fw-semibold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                  disabled={sendingTest || !testPhone}
                >
                  {sendingTest ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      चाचणी संदेश पाठवत आहे...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send-check"></i> चाचणी संदेश पाठवा (Send Test)
                    </>
                  )}
                </button>

                {testFeedback && (
                  <div className={`alert alert-${testFeedback.type} py-2 px-3 mt-3 small shadow-sm mb-0`}>
                    {testFeedback.msg}
                  </div>
                )}
              </form>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default WhatsAppSettings;
